"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Bus, Users, Navigation, Clock, Zap, MapPin, Route } from "lucide-react"

interface User {
  id: string
  name: string
  location: { x: number; y: number }
  distance: string
}

interface BusData {
  id: string
  route: string
  location: { x: number; y: number }
  passengers: number
  eta: string
}

interface AdminLocation {
  id: string
  name: string
  location: { x: number; y: number }
}

const adminLocation: AdminLocation = {
  id: "admin-1",
  name: "Control Center",
  location: { x: 15, y: 20 },
}

const users: User[] = [
  { id: "1", name: "John Doe", location: { x: 20, y: 30 }, distance: "0.8 mi" },
  { id: "2", name: "Sarah Wilson", location: { x: 60, y: 45 }, distance: "1.2 mi" },
  { id: "3", name: "Mike Johnson", location: { x: 40, y: 70 }, distance: "0.5 mi" },
  { id: "4", name: "Emma Davis", location: { x: 80, y: 25 }, distance: "1.5 mi" },
]

const buses: BusData[] = [
  { id: "bus-1", route: "Route A", location: { x: 45, y: 50 }, passengers: 12, eta: "5 min" },
  { id: "bus-2", route: "Route B", location: { x: 70, y: 35 }, passengers: 8, eta: "8 min" },
  { id: "bus-3", route: "Route C", location: { x: 25, y: 65 }, passengers: 15, eta: "3 min" },
]

// Calculate distance between two points (simplified for demo)
const calculateDistance = (point1: { x: number; y: number }, point2: { x: number; y: number }) => {
  const dx = point2.x - point1.x
  const dy = point2.y - point1.y
  const distance = Math.sqrt(dx * dx + dy * dy) * 0.05 // Scale factor for realistic distances
  return distance.toFixed(1)
}

// Generate route path between two points with proper positioning
const generateRoutePath = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  containerWidth: number,
  containerHeight: number,
) => {
  const startX = (start.x / 100) * containerWidth
  const startY = ((start.y * 0.8) / 100) * containerHeight
  const endX = (end.x / 100) * containerWidth
  const endY = ((end.y * 0.8) / 100) * containerHeight

  // Create a more pronounced curved path
  const midX = (startX + endX) / 2
  const midY = Math.min(startY, endY) - 40 // Curve upward more prominently

  return {
    path: `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`,
    midPoint: { x: midX, y: midY + 20 }, // Adjust label position
    startPoint: { x: startX, y: startY },
    endPoint: { x: endX, y: endY },
  }
}

// Helper function to check if screen is small
const useIsSmallScreen = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640) // sm breakpoint
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])
  
  return isSmallScreen
}

// Helper function to get smart tooltip positioning
const getTooltipPosition = (
  elementX: number, 
  elementY: number, 
  containerWidth: number, 
  containerHeight: number,
  isSmallScreen: boolean
) => {
  const actualX = (elementX / 100) * containerWidth
  const actualY = ((elementY * 0.8) / 100) * containerHeight
  
  // On small screens, use simpler positioning to avoid overlaps
  if (isSmallScreen) {
    // Position tooltips more strategically on small screens
    if (actualX < containerWidth * 0.3) {
      return { position: 'right', offset: 'translate(100%, -50%)' }
    } else if (actualX > containerWidth * 0.7) {
      return { position: 'left', offset: 'translate(-100%, -50%)' }
    } else if (actualY < containerHeight * 0.4) {
      return { position: 'bottom', offset: 'translate(-50%, 100%)' }
    } else {
      return { position: 'top', offset: 'translate(-50%, -100%)' }
    }
  }
  
  // Default positioning for larger screens
  return { position: 'top', offset: 'translate(-50%, -100%)' }
}

export default function EnhancedRoutePlanningDemo() {
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedBus, setSelectedBus] = useState<string | null>(null)
  const [zoomedIn, setZoomedIn] = useState(false)
  const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: 350 })
  const isSmallScreen = useIsSmallScreen()

  const steps = ["User Location Tracking", "Admin Dashboard View", "Bus Selection", "Route Optimization"]

  useEffect(() => {
    const updateDimensions = () => {
      const container = document.getElementById("map-container")
      if (container) {
        setContainerDimensions({
          width: container.offsetWidth,
          height: container.offsetHeight,
        })
      }
    }

    updateDimensions()
    window.addEventListener("resize", updateDimensions)
    return () => window.removeEventListener("resize", updateDimensions)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        const next = (prev + 1) % 4
        if (next === 2) {
          setTimeout(() => setSelectedBus("bus-1"), 1000)
        } else if (next === 3) {
          setTimeout(() => setZoomedIn(true), 500)
        } else {
          setSelectedBus(null)
          setZoomedIn(false)
        }
        return next
      })
    }, 4000)

    return () => clearInterval(interval)
  }, [])

  const selectedBusData = buses.find((bus) => bus.id === selectedBus)
  const routeDistance = selectedBusData ? calculateDistance(adminLocation.location, selectedBusData.location) : "0"

  return (
    <div className="relative w-full mx-auto my-6 px-4 sm:px-6 lg:px-8">
      {/* Top Header */}
      <div className="text-center mb-4">
        <h2 className="font-black tracking-tight mb-3" style={{ fontSize: "clamp(28px, 4vw, 48px)", color: "#1D3047" }}>
          <span style={{ color: "#2D5F9E" }}>Smart Transportation </span>
          <span style={{ color: "#E3B02E" }}>Dashboard</span>
        </h2>
        <p className="max-w-3xl mx-auto text-gray-600 text-sm sm:text-base">
          Experience our real-time route optimization system in action.
        </p>
      </div>

      {/* Main Container */}
      <div className="relative w-full max-w-7xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Container Header */}
        <div className="bg-gradient-to-r from-[#1D3047] to-[#5175A5] text-white p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
            <div className="mb-2 sm:mb-0">
              <h2 className="text-lg sm:text-xl font-serif font-semibold mb-1">
                Live <span className="text-[#E3B02E]">System Demo</span>
              </h2>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-2 py-1 text-xs sm:text-sm">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-[#E3B02E]" />
              <span>Simulation Active</span>
            </div>
          </div>
        </div>

        {/* Enhanced Step Indicator */}
        <div className="bg-gray-50 px-3 py-3 border-b border-gray-200">
          <div className="flex flex-col">
            <div className="flex justify-between mb-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 mx-1 rounded-full transition-all duration-500 ${
                    index <= currentStep ? "bg-[#254874]" : "bg-gray-200"
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center transition-all duration-500 ${
                    index === currentStep ? "text-[#1D3047] font-medium" : "text-gray-500"
                  }`}
                >
                  <div
                    className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center mb-1 transition-all duration-500 ${
                      index === currentStep
                        ? "bg-[#E3B02E] text-white scale-105 sm:scale-110"
                        : index < currentStep
                          ? "bg-[#E3B02E]/20 text-[#1D3047]"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    <span className="text-xs sm:text-sm">{index + 1}</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-center px-1 leading-tight">
                    {isSmallScreen ? step.split(" ")[0] : step}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          id="map-container"
          className="relative h-64 sm:h-72 md:h-[350px] bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden"
        >
          {/* Background Map */}
          <div
            className="absolute inset-0 opacity-20 bg-cover bg-center transition-transform duration-1000"
            style={{
              backgroundImage: "url(/placeholder.svg?height=400&width=800&query=city+map+with+streets+and+buildings)",
              transform: zoomedIn ? "scale(2.5) translate(-10%, -10%)" : "scale(1)",
            }}
          />

          {/* Route Line SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
            <defs>
              <marker id="arrowhead" markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto">
                <polygon points="0 0, 12 4, 0 8" fill="#E3B02E" />
              </marker>
              <marker id="startMarker" markerWidth="8" markerHeight="8" refX="4" refY="4">
                <circle cx="4" cy="4" r="3" fill="#dc2626" stroke="white" strokeWidth="1" />
              </marker>
              <marker id="endMarker" markerWidth="8" markerHeight="8" refX="4" refY="4">
                <circle cx="4" cy="4" r="3" fill="#E3B02E" stroke="white" strokeWidth="1" />
              </marker>
            </defs>

            {/* Animated Route Line from Admin to Selected Bus */}
            <AnimatePresence>
              {selectedBus &&
                selectedBusData &&
                (() => {
                  const routeData = generateRoutePath(
                    adminLocation.location,
                    selectedBusData.location,
                    containerDimensions.width,
                    containerDimensions.height,
                  )
                  return (
                    <motion.g
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* Main route path */}
                      <motion.path
                        d={routeData.path}
                        stroke="#E3B02E"
                        strokeWidth={isSmallScreen ? "3" : "4"}
                        fill="none"
                        strokeDasharray="10,5"
                        markerStart="url(#startMarker)"
                        markerEnd="url(#endMarker)"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        exit={{ pathLength: 0, opacity: 0 }}
                        transition={{ duration: 2, ease: "easeInOut" }}
                      />

                      {/* Animated dots along the path - reduced on small screens */}
                      {!isSmallScreen && (
                        <motion.circle
                          r="3"
                          fill="#E3B02E"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: [0, 1, 0],
                            offsetDistance: ["0%", "100%"],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "linear",
                          }}
                          style={{
                            offsetPath: `path('${routeData.path}')`,
                            offsetRotate: "0deg",
                          }}
                        />
                      )}

                      {/* Distance label with enhanced styling */}
                      <motion.g
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{ delay: 1.2, duration: 0.5 }}
                      >
                        <rect
                          x={routeData.midPoint.x - (isSmallScreen ? 25 : 35)}
                          y={routeData.midPoint.y - (isSmallScreen ? 10 : 12)}
                          width={isSmallScreen ? "50" : "70"}
                          height={isSmallScreen ? "20" : "24"}
                          rx={isSmallScreen ? "10" : "12"}
                          fill="white"
                          stroke="#E3B02E"
                          strokeWidth="2"
                          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                        />
                        <text
                          x={routeData.midPoint.x}
                          y={routeData.midPoint.y + (isSmallScreen ? 1 : 2)}
                          textAnchor="middle"
                          className={`${isSmallScreen ? 'text-xs' : 'text-sm'} font-bold fill-[#1D3047]`}
                        >
                          {routeDistance} mi
                        </text>
                      </motion.g>

                      {/* Connection indicators - simplified on small screens */}
                      <motion.g
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: 0.8, duration: 0.5 }}
                      >
                        {/* Admin end indicator */}
                        <circle
                          cx={routeData.startPoint.x}
                          cy={routeData.startPoint.y}
                          r={isSmallScreen ? "6" : "8"}
                          fill="none"
                          stroke="#dc2626"
                          strokeWidth="2"
                          strokeDasharray="4,2"
                        >
                          <animate 
                            attributeName="r" 
                            values={isSmallScreen ? "6;9;6" : "8;12;8"} 
                            dur="2s" 
                            repeatCount="indefinite" 
                          />
                        </circle>

                        {/* Bus end indicator */}
                        <circle
                          cx={routeData.endPoint.x}
                          cy={routeData.endPoint.y}
                          r={isSmallScreen ? "6" : "8"}
                          fill="none"
                          stroke="#E3B02E"
                          strokeWidth="2"
                          strokeDasharray="4,2"
                        >
                          <animate 
                            attributeName="r" 
                            values={isSmallScreen ? "6;9;6" : "8;12;8"} 
                            dur="2s" 
                            repeatCount="indefinite" 
                          />
                        </circle>
                      </motion.g>
                    </motion.g>
                  )
                })()}
            </AnimatePresence>
          </svg>

          {/* Admin Location */}
          <AnimatePresence>
            {currentStep >= 1 && (
              <motion.div
                className="absolute z-30"
                style={{
                  left: `${adminLocation.location.x}%`,
                  top: `${adminLocation.location.y * 0.8}%`,
                  transform: "translate(-50%, -50%)",
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: 1,
                  scale: selectedBus ? (isSmallScreen ? 1.1 : 1.2) : 1,
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative">
                  <div
                    className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center shadow-lg border-2 border-white transition-all duration-500 ${
                      selectedBus ? "bg-red-600 ring-2 ring-red-300" : "bg-red-500"
                    }`}
                  >
                    <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-white" />
                  </div>
                  <motion.div
                    className={`absolute ${
                      isSmallScreen ? '-top-6 -right-2' : '-top-8 sm:-top-10'
                    } ${
                      isSmallScreen ? '' : 'left-1/2 transform -translate-x-1/2'
                    } bg-white rounded px-1.5 py-1 shadow-md text-[10px] sm:text-xs whitespace-nowrap border border-red-200 z-40`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: currentStep >= 1 ? 1 : 0, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="font-medium text-red-600">
                      {isSmallScreen ? "Control" : "Control Center"}
                    </div>
                    <div className="text-gray-500 text-[9px] sm:text-[10px]">
                      {selectedBus ? "Connected" : "Monitoring"}
                    </div>
                  </motion.div>
                  {/* Enhanced pulse animation for admin when connected - reduced on small screens */}
                  <motion.div
                    className={`absolute inset-0 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded-full ${
                      selectedBus ? "bg-red-600" : "bg-red-500"
                    }`}
                    animate={{
                      scale: selectedBus 
                        ? (isSmallScreen ? [1, 1.3, 1] : [1, 1.6, 1]) 
                        : (isSmallScreen ? [1, 1.2, 1] : [1, 1.4, 1]),
                      opacity: [0.7, 0, 0.7],
                    }}
                    transition={{
                      duration: selectedBus ? 1.5 : 2,
                      repeat: Number.POSITIVE_INFINITY,
                    }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Users */}
          <AnimatePresence>
            {currentStep >= 0 &&
              users.map((user, index) => {
                const tooltipPos = getTooltipPosition(
                  user.location.x, 
                  user.location.y, 
                  containerDimensions.width, 
                  containerDimensions.height,
                  isSmallScreen
                )
                
                return (
                  <motion.div
                    key={user.id}
                    className="absolute z-10"
                    style={{
                      left: `${user.location.x}%`,
                      top: `${user.location.y * 0.8}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: currentStep >= 1 || selectedBus ? 0.7 : 1,
                      scale: 1,
                    }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <div className="relative">
                      <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 bg-[#E3B02E] rounded-full flex items-center justify-center shadow-md">
                        <Users className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 text-white" />
                      </div>
                      <motion.div
                        className={`absolute bg-white rounded px-1.5 py-1 shadow-md text-[10px] sm:text-xs whitespace-nowrap z-30 ${
                          isSmallScreen ? 'max-w-[80px]' : ''
                        }`}
                        style={{
                          transform: tooltipPos.offset,
                          ...(tooltipPos.position === 'right' && { left: '100%', top: '50%' }),
                          ...(tooltipPos.position === 'left' && { right: '100%', top: '50%' }),
                          ...(tooltipPos.position === 'bottom' && { left: '50%', top: '100%' }),
                          ...(tooltipPos.position === 'top' && { left: '50%', bottom: '100%' }),
                        }}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: currentStep === 0 ? 1 : 0, y: 0 }}
                        transition={{ delay: index * 0.2 + 0.5 }}
                      >
                        <div className="font-medium text-[#1D3047] truncate">
                          {isSmallScreen ? user.name.split(" ")[0] : user.name.split(" ")[0]}
                        </div>
                        <div className="text-gray-500 text-[9px] sm:text-[10px]">{user.distance}</div>
                      </motion.div>
                      {/* Pulse animation - reduced on small screens */}
                      <motion.div
                        className="absolute inset-0 w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 bg-[#E3B02E] rounded-full"
                        animate={{ 
                          scale: isSmallScreen ? [1, 1.2, 1] : [1, 1.3, 1], 
                          opacity: [0.7, 0, 0.7] 
                        }}
                        transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                      />
                    </div>
                  </motion.div>
                )
              })}
          </AnimatePresence>

          {/* Buses */}
          <AnimatePresence>
            {currentStep >= 1 &&
              buses.map((bus, index) => {
                const tooltipPos = getTooltipPosition(
                  bus.location.x, 
                  bus.location.y, 
                  containerDimensions.width, 
                  containerDimensions.height,
                  isSmallScreen
                )
                
                return (
                  <motion.div
                    key={bus.id}
                    className="absolute z-15"
                    style={{
                      left: `${bus.location.x}%`,
                      top: `${bus.location.y * 0.8}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: 1,
                      scale: selectedBus === bus.id ? (isSmallScreen ? 1.2 : 1.4) : 1,
                      zIndex: selectedBus === bus.id ? 25 : 15,
                    }}
                    transition={{ delay: index * 0.3 }}
                  >
                    <div className="relative">
                      <div
                        className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-md flex items-center justify-center shadow-md transition-all duration-500 ${
                          selectedBus === bus.id ? "bg-[#E3B02E] ring-2 sm:ring-4 ring-[#E3B02E]/30 shadow-lg" : "bg-[#1D3047]"
                        }`}
                      >
                        <Bus className="w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 text-white" />
                      </div>
                      <motion.div
                        className={`absolute bg-white rounded px-1.5 py-1 shadow-md text-[10px] sm:text-xs whitespace-nowrap border z-30 ${
                          isSmallScreen ? 'max-w-[100px]' : ''
                        }`}
                        style={{
                          borderColor: selectedBus === bus.id ? "#E3B02E" : "#e5e7eb",
                          transform: tooltipPos.offset,
                          ...(tooltipPos.position === 'right' && { left: '100%', top: '50%' }),
                          ...(tooltipPos.position === 'left' && { right: '100%', top: '50%' }),
                          ...(tooltipPos.position === 'bottom' && { left: '50%', top: '100%' }),
                          ...(tooltipPos.position === 'top' && { left: '50%', bottom: '100%' }),
                        }}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{
                          opacity: currentStep === 1 || selectedBus === bus.id ? 1 : 0,
                          y: 0,
                        }}
                        transition={{ delay: index * 0.3 + 0.5 }}
                      >
                        <div className="font-medium text-[#1D3047]">{bus.route}</div>
                        {!isSmallScreen && (
                          <div className="text-gray-500 text-[9px] sm:text-[10px]">{bus.passengers} passengers</div>
                        )}
                        <div className="text-[#E3B02E] font-medium text-[9px] sm:text-[10px]">ETA: {bus.eta}</div>
                        {selectedBus === bus.id && !isSmallScreen && (
                          <div className="text-red-600 font-bold text-[8px] border-t pt-0.5 mt-0.5">
                            ← {calculateDistance(adminLocation.location, bus.location)} mi → Control
                          </div>
                        )}
                      </motion.div>
                      {/* Enhanced selection indicator - reduced on small screens */}
                      {selectedBus === bus.id && (
                        <>
                          <motion.div
                            className="absolute inset-0 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-[#E3B02E] rounded-md"
                            animate={{ scale: isSmallScreen ? [1, 1.1, 1] : [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY }}
                          />
                          <motion.div
                            className="absolute inset-0 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-[#E3B02E] rounded-md"
                            animate={{ 
                              scale: isSmallScreen ? [1, 1.2, 1] : [1, 1.4, 1], 
                              opacity: [0.3, 0, 0.3] 
                            }}
                            transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
                          />
                        </>
                      )}
                    </div>
                  </motion.div>
                )
              })}
          </AnimatePresence>

          {/* Info Panels - Improved positioning for small screens */}
          <AnimatePresence>
            {currentStep === 1 && (
              <motion.div
                className={`absolute bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-md z-40 ${
                  isSmallScreen 
                    ? 'top-2 right-2 max-w-[120px]' 
                    : 'top-1 right-1 max-w-[140px] sm:max-w-[160px]'
                }`}
                initial={{ opacity: 0, x: 80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 80 }}
              >
                <h3 className={`font-semibold text-[#1D3047] mb-1 flex items-center ${
                  isSmallScreen ? 'text-[10px]' : 'text-xs'
                }`}>
                  <Navigation className={`${isSmallScreen ? 'w-2 h-2' : 'w-2.5 h-2.5'} mr-1`} />
                  {isSmallScreen ? "Dashboard" : "Admin Dashboard"}
                </h3>
                <div className={`space-y-1 ${isSmallScreen ? 'text-[9px]' : 'text-[9px] sm:text-xs'}`}>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Users:</span>
                    <span className="font-medium text-[#1D3047]">{users.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Buses:</span>
                    <span className="font-medium text-[#1D3047]">{buses.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Control:</span>
                    <span className="font-medium text-red-600">Active</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selectedBus && (
              <motion.div
                className={`absolute bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-md z-40 ${
                  isSmallScreen 
                    ? 'top-2 left-2 max-w-[120px]' 
                    : 'top-1 left-1 max-w-[140px] sm:max-w-[160px]'
                }`}
                initial={{ opacity: 0, x: -80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -80 }}
              >
                <h3 className={`font-semibold text-[#1D3047] mb-1 flex items-center ${
                  isSmallScreen ? 'text-[10px]' : 'text-xs'
                }`}>
                  <Route className={`${isSmallScreen ? 'w-2 h-2' : 'w-2.5 h-2.5'} mr-1`} />
                  {isSmallScreen ? "Route" : "Route Analysis"}
                </h3>
                <div className={`space-y-1 ${isSmallScreen ? 'text-[9px]' : 'text-[9px] sm:text-xs'}`}>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Distance:</span>
                    <span className="font-medium text-[#E3B02E]">{routeDistance} mi</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bus:</span>
                    <span className="font-medium text-[#1D3047]">{selectedBusData?.route}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-green-600">Connected</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {currentStep === 3 && (
              <motion.div
                className={`absolute bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-md z-40 ${
                  isSmallScreen 
                    ? 'bottom-2 left-2 max-w-[120px]' 
                    : 'bottom-1 left-1 max-w-[140px] sm:max-w-[160px]'
                }`}
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 80 }}
              >
                <h3 className={`font-semibold text-[#1D3047] mb-1 flex items-center ${
                  isSmallScreen ? 'text-[10px]' : 'text-xs'
                }`}>
                  <Clock className={`${isSmallScreen ? 'w-2 h-2' : 'w-2.5 h-2.5'} mr-1`} />
                  {isSmallScreen ? "Optimized" : "Route Optimized"}
                </h3>
                <div className={`space-y-1 ${isSmallScreen ? 'text-[9px]' : 'text-[9px] sm:text-xs'}`}>
                  <div className="text-gray-600">
                    Time: <span className="text-[#E3B02E] font-medium">-12m</span>
                  </div>
                  <div className="text-gray-600">
                    Fuel: <span className="text-green-600 font-medium">+15%</span>
                  </div>
                  <div className="text-gray-600">
                    Efficiency: <span className="text-blue-600 font-medium">+23%</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom Status Bar */}
        <div className="bg-gray-50 px-3 sm:px-4 py-2 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs sm:text-sm text-gray-600">
            <div className="mb-1 sm:mb-0">
              <span className="font-medium text-[#1D3047]">Current Step:</span> {steps[currentStep]}
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-[#E3B02E] rounded-full"></div>
                <span>Users: {users.length}</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-[#1D3047] rounded-full"></div>
                <span>Buses: {buses.length}</span>
              </div>
              {selectedBus && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Route: {routeDistance} mi</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

